// Header Component - Streamlined header with sidebar toggle, notifications, and user controls

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { notificationService } from '../../services/notifications';
import { Button } from '../common';
import NotificationPanel from '../portal/NotificationPanel';
import { ViewAsDropdown } from './ViewAsDropdown';
import { ImpersonationBanner } from './ImpersonationBanner';
import styles from './Header.module.css';

// ChemMasters Logo — real brand image
function Logo() {
  return (
    <img
      src="/chemmasters-logo.png"
      alt="ChemMasters — Quality, Innovation, Service"
      className={styles.logo}
    />
  );
}

interface HeaderProps {
  onToggleSidebar?: () => void;
  sidebarCollapsed?: boolean;
}

export function Header({ onToggleSidebar, sidebarCollapsed }: HeaderProps) {
  const { isAuthenticated, user, logout, isImpersonating } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const count = await notificationService.getUnreadCount();
      setUnreadCount(count);
    } catch {
      // Silent fail - badge just won't show
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <>
      <header className={styles.header}>
        <div className={styles.container}>
          <div className={styles.leftSection}>
            {/* Sidebar toggle */}
            {isAuthenticated && onToggleSidebar && (
              <button
                className={styles.toggleButton}
                onClick={onToggleSidebar}
                type="button"
                aria-label={sidebarCollapsed ? 'Open navigation' : 'Close navigation'}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>
            )}

            <Link to="/" className={styles.brand}>
              <Logo />
            </Link>
          </div>

          <nav className={styles.nav}>
            {isAuthenticated ? (
              <>
                {/* Notification bell */}
                <button
                  className={styles.bellButton}
                  onClick={() => setShowNotifications(!showNotifications)}
                  type="button"
                  aria-label="Notifications"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  {unreadCount > 0 && (
                    <span className={styles.bellBadge}>
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* View As dropdown (admin only, not while impersonating) */}
                {isAdmin && !isImpersonating && <ViewAsDropdown />}

                {/* User section */}
                <div className={styles.userSection}>
                  <span className={styles.userName}>{user?.name}</span>
                  <Button variant="ghost" size="sm" onClick={handleLogout}>
                    Logout
                  </Button>
                </div>
              </>
            ) : (
              <Link to="/login">
                <Button variant="primary" size="sm">
                  Sign In
                </Button>
              </Link>
            )}
          </nav>
        </div>
      </header>

      <ImpersonationBanner />

      <NotificationPanel
        isOpen={showNotifications}
        onClose={() => {
          setShowNotifications(false);
          fetchUnreadCount();
        }}
      />
    </>
  );
}
